import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/nos-metiers", label: "Nos métiers" },
  { to: "/comment-ca-marche", label: "Comment ça marche" },
  { to: "/blog", label: "Blog" },
  { to: "/rama-deco", label: "RAMA DÉCO" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();
  const onHome = loc.pathname === "/";
  const light = onHome && !scrolled; // header sur hero sombre

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const bg = scrolled || !onHome
    ? "bg-ivory/85 backdrop-blur-xl border-b border-black/10"
    : "bg-transparent border-b border-transparent";

  const linkColor = light ? "text-ivory/90 hover:text-ivory" : "text-anthracite hover:text-bordeaux-700";
  const activeColor = light ? "text-ivory" : "text-bordeaux-700";

  return (
    <header data-testid="site-header" data-surface={light ? "dark" : "light"} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${bg}`}>
      <div className="wrap flex items-center justify-between h-[72px] md:h-[88px] gap-6">
        <Link to="/" data-testid="header-logo" aria-label="RAMA DÉCO — Construction & Rénovation" className="flex items-center flex-shrink-0">
          <img
            src={light ? "/brand/logo-nom-ivory.png" : "/brand/logo-nom-bordeaux.png"}
            alt="RAMA DÉCO"
            className="brand-logo h-9 md:h-11 transition-opacity duration-500"
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-3 2xl:gap-6 flex-1 justify-center">
          {NAV.filter(n => n.to !== "/").map(n => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replace(/\//g, "") || "home"}`}
              className={`link-underline whitespace-nowrap text-[0.68rem] 2xl:text-[0.74rem] tracking-[0.12em] uppercase font-semibold transition-colors ${
                loc.pathname === n.to ? activeColor : linkColor
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="hidden md:flex items-center gap-5">
            <Link
              to="/mon-projet"
              data-testid="cta-mon-projet-header"
              className={`text-[0.65rem] tracking-[0.14em] uppercase font-medium underline-offset-2 hover:underline transition-colors whitespace-nowrap ${
                light ? "text-ivory/70 hover:text-ivory" : "text-ink-soft hover:text-anthracite"
              }`}
            >
              Mon projet →
            </Link>
            <button
              data-testid="cta-estimer-header"
              onClick={() => nav("/estimation")}
              className={`whitespace-nowrap text-sm inline-flex items-center gap-2 px-5 py-3 rounded-rama-sm font-semibold transition ${
                light
                  ? "bg-ivory text-anthracite hover:bg-white"
                  : "bg-bordeaux-700 text-ivory hover:bg-bordeaux-900"
              }`}
            >
              Estimer mon projet
            </button>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            className={`xl:hidden p-2 ${light ? "text-ivory" : "text-anthracite"}`}
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="xl:hidden bg-ivory border-t border-black/10 py-6">
          <div className="wrap flex flex-col gap-4">
            {NAV.map(n => (
              <Link key={n.to} to={n.to} className="text-lg font-display text-anthracite py-2">{n.label}</Link>
            ))}
            <button data-testid="cta-estimer-mobile-menu" onClick={() => nav("/estimation")} className="btn-primary mt-2 justify-center">
              Estimer mon projet
            </button>
            <Link to="/mon-projet" data-testid="cta-mon-projet-mobile-menu" className="text-center text-sm text-ink-soft underline mt-1">
              Accéder à mon projet →
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
